import { AlertTriangle, CheckCircle2, Mic, ScanFace, Scissors, Timer } from "lucide-react";
import { fmtDuration, mediaSrc } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-xl bg-muted/60 p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-muted-foreground text-xs"><Icon size={12} /> {label}</div>
    <p className="font-mono font-semibold mt-1">{value}</p>
  </div>
);

export const SourceAnalysis = ({ project, capability }) => {
  const analyzing = project.status === "analyzing";
  const a = project.analysis || {};
  return (
    <div className="card-surface p-6 space-y-5" data-testid="source-analysis">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="overline-label">Source</span>
          <p className="text-lg font-semibold tracking-tight mt-1 truncate" data-testid="project-title">{project.title}</p>
          <p className="text-xs font-mono text-muted-foreground">{project.meta.width}×{project.meta.height} · {project.meta.fps} fps · {project.meta.codec}</p>
        </div>
        <span className={`chip ${analyzing ? "chip-idle" : "chip-active"} text-xs`} data-testid="project-status">
          {analyzing && <span className="h-2 w-2 rounded-full bg-primary pulse-dot" />}
          {analyzing ? "Analysing" : "Ready"}
        </span>
      </div>
      {project.thumb_url ? (
        <img src={mediaSrc(project.thumb_url)} alt="" className="w-full rounded-xl border border-border object-cover max-h-44" data-testid="source-thumbnail" />
      ) : (
        <div className="w-full h-36 rounded-xl shimmer" />
      )}
      <div className="grid grid-cols-4 gap-2">
        <Stat icon={Timer} label="Duration" value={fmtDuration(project.meta.duration)} testid="stat-duration" />
        <Stat icon={Scissors} label="Scene cuts" value={analyzing ? "…" : (a.scenes || []).length} testid="stat-scenes" />
        <Stat icon={Mic} label="Speech" value={analyzing ? "…" : (a.transcript || []).length ? `${a.transcript.length} segs` : "none"} testid="stat-speech" />
        <Stat icon={ScanFace} label="Faces" value={analyzing ? "…" : a.faces ? `${Math.round((a.faces.coverage || 0) * 100)}%` : "n/a"} testid="stat-faces" />
      </div>
      {!analyzing && a.faces && a.faces.coverage < 0.15 && (
        <p className="text-xs text-muted-foreground" data-testid="face-tracking-note">Faces appear in under 15% of frames, so smart framing falls back to fixed crops for this source.</p>
      )}
      {a.transcript_error && <p className="text-xs text-amber-500" data-testid="transcript-error">Transcription unavailable: {a.transcript_error}</p>}
      {capability && (
        <div className={`rounded-xl border p-4 text-sm ${capability.sufficient ? "border-primary/40 bg-primary/5" : "border-amber-500/50 bg-amber-500/10"}`} data-testid="capability-banner">
          <div className="flex items-start gap-2">
            {capability.sufficient ? <CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /> : <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />}
            <div>
              <p className="font-semibold">{capability.sufficient ? `Source supports ${capability.requested} meaningful variations` : "Limited variation potential"}</p>
              <p className="text-muted-foreground text-xs mt-1 leading-relaxed">
                {capability.warning || `${capability.distinct_windows} distinct moments detected · up to ${capability.max_meaningful} unique combinations.`}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
